/*
 * Proprietary and confidential | 2026 | SoundTalks NV
 */

import { fromJS } from 'immutable';
import * as constants from 'data/constants/admin/alert-rules';
import LoadingProgress from 'data/utils/reducers/loading';
import Pagination from 'data/utils/reducers/pagination';

export const itemsPagination = new Pagination('adminAlertRules');
export const loadingProgress = new LoadingProgress('adminAlertRule');
export const listLoadingProgress = new LoadingProgress('adminAlertRulesList');
export const createLoadingProgress = new LoadingProgress('adminAlertRuleCreate');
export const updateLoadingProgress = new LoadingProgress('adminAlertRuleUpdate');
export const versionsListLoadingProgress = new LoadingProgress('adminAlertRuleVersionsList');
export const versionCreateLoadingProgress = new LoadingProgress('adminAlertRuleVersionCreate');

// Two entity types (alertRules, alertRuleVersions) share this reducer, so `entities`
// is nested by entity name rather than the single flat map used by single-entity slices.
const mergeData = (state: any, payload: any) =>
  state.withMutations((newState: any) => {
    const { entities } = payload;

    if (entities?.alertRules) {
      newState.mergeIn(['entities', 'alertRules'], fromJS(entities.alertRules));
    }
    if (entities?.alertRuleVersions) {
      newState.mergeIn(['entities', 'alertRuleVersions'], fromJS(entities.alertRuleVersions));
    }
  });

const loadList = (state: any, action: any) =>
  state.withMutations((newState: any) => {
    const { result, perPage, total } = action.payload;
    mergeData(newState, action.payload);
    itemsPagination.set(newState, total, perPage, fromJS(result));
    listLoadingProgress.setLoaded(newState);
  });

const loadEntity = (state: any, action: any) =>
  state.withMutations((newState: any) => {
    mergeData(newState, action.payload);
    newState.set('currentRuleId', action.payload.result);
    loadingProgress.setLoaded(newState);
  });

const loadVersionsList = (state: any, action: any) =>
  state.withMutations((newState: any) => {
    const { ruleId, result } = action.payload;
    mergeData(newState, action.payload);
    newState.setIn(['versionsByRuleId', String(ruleId)], fromJS(result));
    versionsListLoadingProgress.setLoaded(newState);
  });

const clear = (state: any) =>
  state.withMutations((newState: any) => {
    listLoadingProgress.clear(newState);
    itemsPagination.clear(newState);
    loadingProgress.clear(newState);
    createLoadingProgress.clear(newState);
    updateLoadingProgress.clear(newState);
    versionsListLoadingProgress.clear(newState);
    versionCreateLoadingProgress.clear(newState);
  });

const clearSingle = (state: any) =>
  state.withMutations((newState: any) => {
    newState.delete('currentRuleId');
    loadingProgress.clear(newState);
    updateLoadingProgress.clear(newState);
  });

const clearVersions = (state: any) =>
  state.withMutations((newState: any) => {
    newState.set('versionsByRuleId', fromJS({}));
    versionsListLoadingProgress.clear(newState);
    versionCreateLoadingProgress.clear(newState);
  });

const initialState = fromJS({
  entities: { alertRules: {}, alertRuleVersions: {} },
  versionsByRuleId: {},
});

export default (state = initialState, action: any) => {
  switch (action.type) {
    case constants.LIST_LOAD_START:
      return listLoadingProgress.setLoading(state);
    case constants.LIST_LOAD_FAILED:
      return listLoadingProgress.setLoadFailed(state);
    case constants.LIST_LOAD_SUCCESS:
      return loadList(state, action);
    case constants.LOAD_START:
      return loadingProgress.setLoading(state);
    case constants.LOAD_FAILED:
      return loadingProgress.setLoadFailed(state);
    case constants.LOAD_SUCCESS:
      return loadEntity(state, action);
    case constants.CREATE_START:
      return createLoadingProgress.setLoading(state);
    case constants.CREATE_FAILED:
      return createLoadingProgress.setLoadFailed(state);
    case constants.CREATE_SUCCESS:
      return state.withMutations((newState: any) => {
        mergeData(newState, action.payload);
        createLoadingProgress.setLoaded(newState);
      });
    case constants.UPDATE_START:
      return updateLoadingProgress.setLoading(state);
    case constants.UPDATE_FAILED:
      return updateLoadingProgress.setLoadFailed(state);
    case constants.UPDATE_SUCCESS:
      return state.withMutations((newState: any) => {
        mergeData(newState, action.payload);
        updateLoadingProgress.setLoaded(newState);
      });
    case constants.VERSIONS_LIST_LOAD_START:
      return versionsListLoadingProgress.setLoading(state);
    case constants.VERSIONS_LIST_LOAD_FAILED:
      return versionsListLoadingProgress.setLoadFailed(state);
    case constants.VERSIONS_LIST_LOAD_SUCCESS:
      return loadVersionsList(state, action);
    case constants.VERSION_CREATE_START:
      return versionCreateLoadingProgress.setLoading(state);
    case constants.VERSION_CREATE_FAILED:
      return versionCreateLoadingProgress.setLoadFailed(state);
    case constants.VERSION_CREATE_SUCCESS:
      return state.withMutations((newState: any) => {
        mergeData(newState, action.payload);
        versionCreateLoadingProgress.setLoaded(newState);
      });
    case constants.CLEAR:
      return clear(state);
    case constants.CLEAR_SINGLE:
      return clearSingle(state);
    case constants.CLEAR_VERSIONS:
      return clearVersions(state);
    default:
      return state;
  }
};
